import { Injectable } from "@angular/core";
import { catchError, Observable, of, ReplaySubject, tap } from "rxjs";
import { environment } from "@env/environment";
import { HttpClient } from "@angular/common/http";
import { Region, RegionRequest } from "./region.interface";

@Injectable()
export class RegionService {

    baseUrl = environment.administration_url + '/admin/regions';
    private _region: ReplaySubject<Region> = new ReplaySubject<Region>(1);
    private _regions: ReplaySubject<Region[]> = new ReplaySubject<Region[]>(1);

    set region(value: Region) {
        this._region.next(value);
    }

    get region$() {
        return this._region.asObservable();
    }

    set regions(value: Region[]) {
        this._regions.next(value);
    }

    get regions$() {
        return this._regions.asObservable();
    }

    constructor(
        private _httpClient: HttpClient
    ) { }

    getAll(): Observable<Region[]> {
        return this._httpClient.get<Region[]>(`${this.baseUrl}`)
        .pipe(
            tap((response: Region[]) => {
                this.regions = response || [];
                return response;
            }),
            catchError(() => of([]))
        );
    }

    getById(uuid: string): Observable<Region> {
        return this._httpClient.get<Region>(`${this.baseUrl}/${uuid}`)
        .pipe(
            tap((region) => {
                this.region = region;
                return region;
            }),
            catchError(() => of({} as Region))
        );
    }

    create(data: RegionRequest): Observable<Region> {
        return this._httpClient.post<Region>(`${this.baseUrl}`, data)
        .pipe(
            tap((region) => {
                this.region = region;
                return region;
            }),
            catchError(() => of({} as Region))
        );
    }

    update(uuid: string, data: RegionRequest): Observable<Region> {
        return this._httpClient.put<Region>(`${this.baseUrl}/${uuid}`, data)
        .pipe(
            tap((region) => {
                this.region = region;
                return region;
            }),
            catchError(() => of({} as Region))
        );
    }

    getByCountryUuid(countryUuid: string): Observable<Region[]> {
        return this._httpClient.get<Region[]>(`${this.baseUrl}/by-country/${countryUuid}`)
        .pipe(
            tap((response: Region[]) => {
                this.regions = response || [];
                return response;
            }),
            catchError(() => of([]))
        );
    }

    delete(uuid: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}/${uuid}`)
        .pipe(
            catchError(() => of(undefined))
        );
    }
}
